import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import legal from '../legal';
import { API } from '../App';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  logo: {
    color: '#e94560',
    fontSize: '36px',
    fontWeight: 'bold',
    letterSpacing: '3px',
    marginBottom: '8px',
  },
  subtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '600px',
    boxSizing: 'border-box',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  cardSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '32px',
  },
  sectionTitle: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '16px',
    marginTop: '24px',
    borderTop: '1px solid #0f3460',
    paddingTop: '24px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '4px',
  },
  label: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '6px',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    marginBottom: '16px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  gameGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '24px',
  },
  gameButton: {
    padding: '12px 16px',
    backgroundColor: '#0f3460',
    border: '2px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    boxSizing: 'border-box',
  },
  gameButtonSelected: {
    padding: '12px 16px',
    backgroundColor: '#0f3460',
    border: '2px solid #e94560',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  error: {
    color: '#e94560',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  success: {
    color: '#00cc00',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  loginLink: {
    color: '#a0a0b0',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '24px',
  },
  link: {
    color: '#e94560',
    textDecoration: 'none',
    fontWeight: 'bold',
  },
  hint: {
    color: '#a0a0b0',
    fontSize: '12px',
    marginTop: '-12px',
    marginBottom: '16px',
  },
};

function Register() {
  const [form, setForm] = useState({
    company_name: '',
    address: '',
    contact_first_name: '',
    contact_last_name: '',
    email: '',
    password: '',
    confirm_password: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleRegister() {
    setError('');
    if (!form.company_name || !form.address || !form.contact_first_name ||
        !form.contact_last_name || !form.email || !form.password) {
      setError('All fields are required');
      return;
    }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: form.company_name,
          address: form.address,
          contact_first_name: form.contact_first_name,
          contact_last_name: form.contact_last_name,
          email: form.email,
          password: form.password,
          accepted_terms: true,
          terms_version: legal.version,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Registration failed');
      } else {
        setSuccess('Account created! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setLoading(false);
  }

  return (
    <div style={styles.container}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '24px',
        width: '300px',
      }}>
        <img src="/logo.png" alt="EZ Pace" style={{ width: '100%', display: 'block' }} />
      </div>
      <div style={styles.subtitle}>Create Your Account</div>
      <div style={styles.card}>
        <div style={styles.title}>Casino Registration</div>
        <div style={styles.cardSubtitle}>
          Set up your EZ Pace account to start tracking dealer efficiency
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        <div style={styles.sectionTitle}>Casino Information</div>
        <label style={styles.label}>Casino / Company Name</label>
        <input
          style={styles.input}
          value={form.company_name}
          onChange={e => update('company_name', e.target.value)}
          placeholder="e.g. Aria Resort & Casino"
        />
        <label style={styles.label}>Address</label>
        <input
          style={styles.input}
          value={form.address}
          onChange={e => update('address', e.target.value)}
          placeholder="123 Casino Blvd, Las Vegas, NV 89101"
        />

        <div style={styles.sectionTitle}>Contact Information</div>
        <div style={styles.grid}>
          <div>
            <label style={styles.label}>First Name</label>
            <input
              style={styles.input}
              value={form.contact_first_name}
              onChange={e => update('contact_first_name', e.target.value)}
              placeholder="First name"
            />
          </div>
          <div>
            <label style={styles.label}>Last Name</label>
            <input
              style={styles.input}
              value={form.contact_last_name}
              onChange={e => update('contact_last_name', e.target.value)}
              placeholder="Last name"
            />
          </div>
        </div>
        <label style={styles.label}>Email Address</label>
        <input
          style={styles.input}
          type="email"
          value={form.email}
          onChange={e => update('email', e.target.value)}
          placeholder="admin@yourcasino.com"
        />
        <label style={styles.label}>Password</label>
        <input
          style={styles.input}
          type="password"
          value={form.password}
          onChange={e => update('password', e.target.value)}
          placeholder="Minimum 8 characters"
        />
        <label style={styles.label}>Confirm Password</label>
        <input
          style={styles.input}
          type="password"
          value={form.confirm_password}
          onChange={e => update('confirm_password', e.target.value)}
          placeholder="Re-enter password"
        />

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', color: '#d0d0e0', fontSize: '14px', lineHeight: 1.5, margin: '8px 0 16px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={acceptedTerms}
            style={{ marginTop: '3px' }}
            onChange={e => setAcceptedTerms(e.target.checked)}
          />
          <span>
            I have read and agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" style={styles.link}>Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" style={styles.link}>Privacy Policy</a>
            {' '}(version {legal.version}), and I am authorized to accept them for my company.
          </span>
        </label>

        <button style={styles.button} onClick={handleRegister} disabled={loading || !acceptedTerms}>
          {loading ? 'Creating account...' : 'Create Account'}
        </button>

        <div style={styles.loginLink}>
          Already have an account? <Link to="/login" style={styles.link}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default Register;
